import { apiProxyRequest as request } from "Api/apiProxyRequest";

import type {
  CarbonEmissionsOrganizationResponse,
  GetOrganizationCarbonEmissionsParams
} from "./carbonEmissions.types";

/**
 * Fetches carbon emissions data for an organization from the metrics API.
 *
 * This function calls the Platform.sh/Upsun carbon emissions metrics endpoint
 * to retrieve aggregated emissions data for all projects in an organization.
 *
 * API Endpoint:
 * - Method: GET
 * - Path: `/organizations/{organizationId}/metrics/carbon`
 * - Query Parameters: from, to, interval
 *
 * @param params - Request parameters
 * @param params.organizationId - UUID of the organization to fetch data for
 * @param params.from - Start date in ISO 8601 format (YYYY-MM-DD)
 * @param params.to - End date in ISO 8601 format (YYYY-MM-DD)
 * @param params.interval - Data aggregation interval: "day", "week", or "month" (default: "day")
 *
 * @returns Promise resolving to API response containing:
 *   - projects: Array of projects with emissions data
 *   - Each project includes: project_id, project_title, organization_id, values array
 *   - Each value includes: start_time (ISO date), value (number in kgCO2e)
 *   - _links: HAL navigation links
 *   - meta: API metadata
 *
 * Response Shape Example:
 * ```json
 * {
 *   "projects": [
 *     {
 *       "project_id": "abc123",
 *       "project_title": "Production",
 *       "organization_id": "org456",
 *       "values": [
 *         { "start_time": "2024-01-01", "value": 123.45 },
 *         { "start_time": "2024-02-01", "value": 234.56 }
 *       ]
 *     }
 *   ],
 *   "_links": { "self": { "href": "..." } },
 *   "meta": { "count": 1 }
 * }
 * ```
 *
 * Error Responses:
 * - 404: Organization not found or metrics feature not available
 * - 403: Insufficient permissions to view organization metrics
 * - 401: Authentication required
 * - 400: Invalid date range or parameters
 *
 * Rate Limiting:
 * - Standard Platform.sh API rate limits apply (see API documentation)
 * - Consider caching responses for repeated requests within the same time range
 * - For frequent polling, use appropriate intervals (e.g., hourly checks for monthly data)
 *
 * Performance Considerations:
 * - Larger date ranges and finer intervals (day vs month) increase response size
 * - Response time scales with number of projects in the organization
 * - Consider using month interval for year-long queries to reduce data transfer
 *
 * @example
 * ```typescript
 * // Fetch previous year monthly data
 * const response = await getOrganizationCarbonEmissions({
 *   organizationId: 'org-uuid-123',
 *   from: '2023-01-01',
 *   to: '2024-01-01',
 *   interval: 'month'
 * });
 * ```
 */
export const getOrganizationCarbonEmissions = ({
  organizationId,
  from,
  to,
  interval = "day"
}: GetOrganizationCarbonEmissionsParams) =>
  request<CarbonEmissionsOrganizationResponse>(
    `/organizations/${organizationId}/metrics/carbon`,
    "GET",
    { from, to, interval }
  );
